import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { MarketEmptyPanel, MarketLoadingPanel } from '~/features/shared/market-content';
import { JobMessage } from '../types';

type Props = {
  messages: JobMessage[];
  currentProviderId: number | undefined;
  loading: boolean;
};

const formatMessageTime = (dateStr: string): string => {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
};

export const MessagesSection = ({ messages, currentProviderId, loading }: Props) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (loading) {
    return <MarketLoadingPanel label="Loading messages" className="flex-1" />;
  }

  if (messages.length === 0) {
    return <MarketEmptyPanel title="No messages yet" className="flex-1" />;
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
      {messages.map((msg) => {
        const isSent =
          currentProviderId !== undefined && msg.senderProviderId === currentProviderId;
        return (
          <div
            key={msg.id}
            className={classNames('max-w-[85%]', isSent ? 'self-end' : 'self-start')}
          >
            <div
              className={classNames(
                // MCV-1576: `#root { text-align: center }` (styles.css) is
                // inherited by every descendant that doesn't set its own
                // text-align, and nothing here did — so bubble text rendered
                // centered. `text-left` must stay on this element (not a
                // parent) because that inherited `center` beats anything we
                // don't override at the bubble itself.
                //
                // Both sides read left-aligned, sent included: QA revised the
                // original left/right convention after it shipped. The bubble
                // still sits on its own side of the column via self-end /
                // self-start above — only the text inside is left-aligned.
                'rounded-md px-4 py-2 text-sm leading-relaxed text-left',
                isSent
                  ? 'bg-primary-50 text-white'
                  : 'border border-grey-900 bg-white text-content-primary'
              )}
            >
              {msg.messageBody}
              <div
                className={classNames(
                  'text-[10px] mt-1',
                  isSent ? 'text-white/70' : 'text-content-secondary'
                )}
              >
                {formatMessageTime(msg.createdAt)}
              </div>
            </div>
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
};
